/**
 * Copyright 2020, ChoTot Ltd. Licensed under the Apache License, Version 2.0.
 *
 * Developer's Notes:
 * This is an example of component whose styles are highly dynamic and
 * dependent on many props.
 *
 * The reason we wrap the StyledComponent with another FunctionComponent is that
 * Storybook will pick up the JSDoc and prop type desription correctly.
 */
import * as React from 'react';
import type { Colors, Radii, Sizes } from '@clad-ui/theme';
import { StyledComponent } from './ShapeIcon.styles';

/**
 * This component allows icons to have background and decorated shape
 *
 * `✅ Stable`
 *
 * @example:
 *
 * ```jsx
 * <ShapeIcon radius="circle" bg="positive" color="white"><SvgHeart /><ShapeIcon>
 * ```
 */
export interface ShapeIconProps extends React.HTMLAttributes<HTMLElement> {
  /** Element name or Component for the icon wrapper to be render as */
  as?: React.ElementType;
  /**
   * Icon size
   * @default "sm"
   */
  size?: Sizes;
  /**
   * Border radius
   * @default "circle"
   */
  radius?: Radii;
  /**
   * Background color, "none" or a theme's color name
   * @default "none"
   */
  bg?: Colors | 'none';
  /**
   * Icon color, one of theme's color name
   * @default "text"
   */
  color?: Colors;
  /**
   * The SVG Icon component to be decorated
   */
  children: React.ReactNode;
}

const ShapeIcon: React.FC<ShapeIconProps> = ({
  as = 'span',
  size = 'sm',
  radius = 'circle',
  bg = 'none',
  color = 'text',
  children,
  ...attrs
}) => (
  <StyledComponent as={as} size={size} radius={radius} bg={bg} color={color} {...attrs}>
    {children}
  </StyledComponent>
);

export default ShapeIcon;
